import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { MENU_ITEMS } from "@/config/menu";
import { SidebarItem } from "./SidebarItem";
import { hasAnyPermission } from "@/permissions/rbac";
import { MODULE_STATE, MODULE_UNAVAILABLE_MESSAGE, getModuleState } from "@/permissions/modules";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/Accordion";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/HoverCard";
import { ButtonShadcn } from "@/components/ui/Button";

// A section "contains" the current page when the page's path is the item's
// path or a deeper path under it (candidate/job detail pages, etc.) — never
// a same-prefix sibling (`/admin/user` must not match `/admin/user-profile`).
const isItemActive = (pathname, item) => !item.external && (pathname === item.to || pathname.startsWith(`${item.to}/`));

/**
 * Renders a vertical menu list for the sidebar with navigation items.
 *
 * Expanded: sections are an accordion (first non-empty section open by
 * default, one at a time) — the same single-column list as always.
 *
 * Collapsed: there is no room for a header or an open/closed list, so each
 * section collapses to its own heading icon (`MENU_ITEMS[i].icon`) in a
 * short rail; hovering it opens a flyout with the section's full, labelled
 * item list, so a specific page is still one hover away instead of
 * expanding the sidebar.
 *
 * @param {boolean} collapsed - Whether the sidebar is collapsed
 * @param {string[]} permissions - Current user permissions
 */
export const SidebarMenu = ({ collapsed, permissions = [] }) => {
  const user = useSelector((state) => state.auth?.user);
  const { pathname } = useLocation();

  const sections = MENU_ITEMS.map((section, i) => {
    const filterMenu = section.items.filter((item) => {
      // Entitlement first, and it is an extra condition rather than a
      // replacement: an item can need both a permission and a module.
      // Only NOT_ENTITLED hides it — a killed module keeps its item, so
      // the page still loads and its existing data still renders. The item
      // renders muted instead, below.
      if (item.module && getModuleState(item.module, user) === MODULE_STATE.NOT_ENTITLED) {
        return false;
      }

      // Item is public to any signed-in user.
      if (item.permissions?.includes("*")) return true;

      // Route through hasAnyPermission rather than a raw Array.includes so
      // wildcard grants held by the *user* (`*`, `job.*`, `*.view`) match
      // too — a tenant admin carrying `*` was previously filtered out here
      // because `["*"].includes("job.view")` is false.
      return hasAnyPermission(item.permissions ?? [], permissions ?? []);
    });

    return { key: `${section.section}.${i}`, label: section.section, icon: section.icon, filterMenu };
  }).filter((section) => section.filterMenu.length > 0);

  const renderItems = (section, itemsCollapsed, itemClassName) => (
    <div className="space-y-0.5">
      {section.filterMenu.map((item) => (
        // A killed module keeps a working link — reading what is already
        // there is exactly what the kill switch leaves open — but the item
        // has to look different from one that works, or the tenant finds
        // out by clicking a dead button on the page.
        <SidebarItem
          key={item.to}
          {...item}
          collapsed={itemsCollapsed}
          unavailable={!!item.module && getModuleState(item.module, user) === MODULE_STATE.UNAVAILABLE}
          unavailableReason={MODULE_UNAVAILABLE_MESSAGE}
          className={itemClassName}
        />
      ))}
    </div>
  );

  if (collapsed) {
    const activeSection = sections.find((section) => section.filterMenu.some((item) => isItemActive(pathname, item)));

    return (
      <div className="flex flex-col gap-0.5">
        {sections.map((section) => {
          const RailIcon = section.icon;
          const isActive = section.key === activeSection?.key;

          return (
            <HoverCard key={section.key} openDelay={100} closeDelay={100}>
              {/* A rail icon stands for a whole section, not one route, so it
                  only opens the flyout — navigation happens from an actual
                  item inside it. */}
              <HoverCardTrigger asChild>
                <ButtonShadcn
                  variant="ghost"
                  size="icon"
                  aria-label={section.label}
                  className={cn(
                    "w-8 h-8 mx-auto rounded-md",
                    isActive ? "bg-background-tertiary text-text-primary" : "text-sidebar-text hover:text-sidebar-hoverText hover:bg-sidebar-hoverBg",
                  )}
                >
                  <RailIcon size={14} />
                </ButtonShadcn>
              </HoverCardTrigger>

              <HoverCardContent>
                <p className="text-[10px] tracking-wider text-text-secondary font-semibold uppercase px-1.5 pb-1.5 mb-1 border-b border-border-primary">
                  {section.label}
                </p>
                {renderItems(section, false, "text-text-primary")}
              </HoverCardContent>
            </HoverCard>
          );
        })}
      </div>
    );
  }

  return (
    <Accordion multiple defaultValue={sections[0] ? [sections[0].key] : []}>
      {sections.map((section) => {
        const SectionIcon = section.icon;

        return (
          <AccordionItem key={section.key} value={section.key} className="border-0 border-b border-border-primary rounded-none bg-transparent mb-3 overflow-visible">
            <AccordionTrigger className="px-0 py-0 mb-2 bg-transparent text-[9px] tracking-wider font-semibold uppercase text-text-secondary hover:text-text-primary">
              <span className="flex items-center gap-1.5">
                <SectionIcon size={12} />
                {section.label}
              </span>
            </AccordionTrigger>

            <AccordionContent className="px-0 pt-0 pb-3">{renderItems(section, false)}</AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
